// ============================================================================
// PROJECTILE ENTITY (for non-hitscan weapons or abilities)
// ============================================================================

import { EntityId, Vector2, ProjectileState } from '@/shared/types';
import { Vec2, generateId } from '@/shared/utils';
import { CollisionBody, ColliderType, CollisionLayers } from '@/engine/CollisionSystem';

export class Projectile {
  public readonly id: EntityId;
  public readonly ownerId: EntityId;
  public position: Vector2;
  public prevPosition: Vector2;
  public velocity: Vector2;
  public damage: number;
  public lifetime: number;
  public spawnTime: number;
  public active: boolean;
  public radius: number;

  constructor(
    ownerId: EntityId,
    position: Vector2,
    direction: Vector2,
    speed: number,
    damage: number,
    lifetime: number,
    spawnTime: number
  ) {
    this.id = generateId('proj');
    this.ownerId = ownerId;
    this.position = { ...position };
    this.prevPosition = { ...position };
    this.velocity = Vec2.mul(Vec2.normalize(direction), speed);
    this.damage = damage;
    this.lifetime = lifetime;
    this.spawnTime = spawnTime;
    this.active = true;
    this.radius = 4;
  }

  update(dt: number, time: number): void {
    if (!this.active) return;

    // Store previous position
    this.prevPosition = { ...this.position };

    // Move
    this.position = Vec2.add(this.position, Vec2.mul(this.velocity, dt / 1000));

    // Check lifetime
    if (time >= this.spawnTime + this.lifetime) {
      this.active = false;
    }
  }

  destroy(): void {
    this.active = false;
  }

  getCollisionBody(): CollisionBody {
    return {
      id: this.id,
      position: this.position,
      velocity: this.velocity,
      collider: {
        type: ColliderType.Circle,
        offset: { x: 0, y: 0 },
        radius: this.radius,
      },
      layer: CollisionLayers.PROJECTILE,
      mask: CollisionLayers.PLAYER | CollisionLayers.ENEMY | CollisionLayers.WALL,
      isStatic: false,
      isTrigger: true,
      userData: this,
    };
  }

  toState(): ProjectileState {
    return {
      id: this.id,
      ownerId: this.ownerId,
      position: { ...this.position },
      velocity: { ...this.velocity },
      damage: this.damage,
      lifetime: this.lifetime,
      spawnTime: this.spawnTime,
    };
  }

  static fromState(state: ProjectileState): Projectile {
    const proj = new Projectile(
      state.ownerId,
      state.position,
      Vec2.normalize(state.velocity),
      Vec2.length(state.velocity),
      state.damage,
      state.lifetime,
      state.spawnTime
    );
    proj.velocity = { ...state.velocity };
    return proj;
  }
}
